import React from 'react';

import type { Company } from '@xing-com/crate-common-graphql-types';

enum ActionKind {
  ADD_COMPANIES = 'ADD_COMPANIES',
  FOLLOW_COMPANY = 'FOLLOW_COMPANY',
  UNFOLLOW_COMPANY = 'UNFOLLOW_COMPANY',
}

type FollowAction = {
  type: ActionKind.FOLLOW_COMPANY | ActionKind.UNFOLLOW_COMPANY;
  payload: {
    entityPageId: string;
  };
};
type AddCompaniesAction = {
  type: ActionKind.ADD_COMPANIES;
  payload: Company[];
};

type Action = FollowAction | AddCompaniesAction;

type CompaniesReducerType = (state: Company[], action: Action) => Company[];

type CompaniesDataContextType = {
  globalCompaniesState: Company[];
  addCompanies: ({ companies }: { companies: Company[] }) => void;
  followCompany: ({ entityPageId }: { entityPageId: string }) => void;
  unfollowCompany: ({ entityPageId }: { entityPageId: string }) => void;
};
export const CompaniesDataContext = React.createContext<
  CompaniesDataContextType | undefined
>(undefined);

const companiesReducer: CompaniesReducerType = (
  state: Company[],
  action: Action
) => {
  switch (action.type) {
    case ActionKind.ADD_COMPANIES: {
      const newCompanies =
        (action as AddCompaniesAction).payload?.filter(
          (newCompany) =>
            !state.some((company) => company?.id === newCompany?.id)
        ) || [];
      return [...state, ...newCompanies];
    }
    case ActionKind.FOLLOW_COMPANY: {
      return state.map((company) => {
        if (company.entityPageId === action.payload.entityPageId) {
          return {
            ...company,
            userContext: {
              followState: {
                isFollowing: true,
              },
            },
          };
        }
        return company;
      });
    }
    case ActionKind.UNFOLLOW_COMPANY: {
      return state.map((company) => {
        if (company.entityPageId === action.payload.entityPageId) {
          return {
            ...company,
            userContext: {
              followState: {
                isFollowing: false,
              },
            },
          };
        }
        return company;
      });
    }
    default:
      return state;
  }
};

export const CompaniesDataProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [globalCompaniesState, dispatch] =
    React.useReducer<CompaniesReducerType>(companiesReducer, []);

  const addCompanies = ({ companies }: { companies: Company[] }) =>
    dispatch({ type: ActionKind.ADD_COMPANIES, payload: companies });

  const followCompany = ({ entityPageId }: { entityPageId: string }) =>
    dispatch({ type: ActionKind.FOLLOW_COMPANY, payload: { entityPageId } });

  const unfollowCompany = ({ entityPageId }: { entityPageId: string }) =>
    dispatch({ type: ActionKind.UNFOLLOW_COMPANY, payload: { entityPageId } });

  const contextData: CompaniesDataContextType = {
    globalCompaniesState,
    addCompanies,
    followCompany,
    unfollowCompany,
  };

  return (
    <CompaniesDataContext.Provider value={contextData}>
      {children}
    </CompaniesDataContext.Provider>
  );
};
